import PropTypes from "prop-types";
// @mui
import { styled } from "@mui/material/styles";
import {
    Box,
    Stack,
    AppBar,
    Toolbar,
    IconButton,
    Tooltip,
} from "@mui/material";
// utils
import { bgBlur } from "../../../utils/cssStyles";
// components
import Iconify from "../../../components/iconify";
import { isAppleDevice } from "../../../components/command-palette/useCommandPalette";
import { useColorMode } from "../../../theme/ColorModeProvider";
import i18n from "../../../i18n";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
//
import AccountPopover from "./AccountPopover";
import LanguagePopover from "./LanguagePopover";

import React from "react";
// ----------------------------------------------------------------------

const NAV_WIDTH = 280;
const COLLAPSED_WIDTH = 66;

const HEADER_MOBILE = 64;

// Was 92px, which bought nothing: the bar holds a two-line title block, an Add
// button and two icon buttons. 64px matches the mobile height and returns ~28px
// of vertical space to the grid on every page.
const HEADER_DESKTOP = 64;

const StyledRoot = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => {
    const navWidth = collapsed ? COLLAPSED_WIDTH : NAV_WIDTH;

    return {
        ...bgBlur({ color: theme.palette.background.default }),
        // AppBar sets its text to primary.contrastText (white) for its own
        // primary fill, which this bar does not have: page actions rendered
        // here in `inherit` (the editor's Back) came out white on white.
        color: theme.palette.text.primary,
        boxShadow: 'none',
        [theme.breakpoints.up('lg')]: {
            width: `calc(100% - ${navWidth + 1}px)`,
        },
    };
});


const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    minHeight: HEADER_MOBILE,
    [theme.breakpoints.up("lg")]: {
        minHeight: HEADER_DESKTOP,
        padding: theme.spacing(0, 5),
    },
}));

// ----------------------------------------------------------------------

Header.propTypes = {
    onOpenNav: PropTypes.func,
    onOpenCommandPalette: PropTypes.func,
    // On a phone, leave the bar's own buttons to the page's menu.
    hideMobileControls: PropTypes.bool,
};


export default function Header({
    onOpenNav,
    actions,
    collapsed,
    onOpenCommandPalette,
    hideMobileControls = false,
}) {
    const shortcutHint = isAppleDevice() ? "⌘K" : "Ctrl+K";
    const { mode, toggle: toggleColorMode } = useColorMode();
    const isDark = mode === "dark";

    return (
        <StyledRoot collapsed={collapsed}>
            <StyledToolbar>
                <IconButton
                    onClick={onOpenNav}
                    sx={{
                        mr: 1,
                        color: "text.primary",
                        display: { lg: "none" },
                    }}
                >
                    <Iconify icon="eva:menu-2-fill" />
                </IconButton>
                {/* The page's title and actions take the room that is left
                    and shrink (the title ellipsises); the bar's own buttons
                    keep their size. On a phone they used to be pushed off the
                    right edge: theme, language and the account menu. */}
                <Box
                    sx={{
                        mr: { xs: hideMobileControls ? 0 : 1, sm: 2 },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: '1 1 auto',
                        minWidth: 0
                    }}
                >
                    {actions}
                </Box>

                {/* On a phone a grid page keeps the bar for its title, Add
                    and ⋮; theme, language and account are in that menu. */}
                <Stack
                    direction="row"
                    alignItems="center"
                    flexShrink={0}
                    spacing={{
                        xs: 0.5,
                        sm: 1.75,
                    }}
                    sx={{
                        display: {
                            xs: hideMobileControls ? 'none' : 'flex',
                            sm: 'flex',
                        },
                    }}
                >
                    {/* The shortcut is the fast path, but it has to be
                        discoverable — and reachable by mouse. */}
                    {onOpenCommandPalette && (
                        <Tooltip
                            title={`${i18n.t("common.searchPages")} (${shortcutHint})`}
                        >
                            <IconButton
                                aria-label="search pages"
                                onClick={onOpenCommandPalette}
                                sx={{ color: "text.primary" }}
                            >
                                <Iconify icon="eva:search-fill" />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip
                        title={i18n.t(
                            isDark
                                ? "common.switchToLight"
                                : "common.switchToDark"
                        )}
                    >
                        <IconButton
                            aria-label="toggle color mode"
                            onClick={toggleColorMode}
                            sx={{ color: "text.primary" }}
                        >
                            {isDark ? (
                                <LightModeOutlinedIcon />
                            ) : (
                                <DarkModeOutlinedIcon />
                            )}
                        </IconButton>
                    </Tooltip>

                    <LanguagePopover />

                    <AccountPopover />
                </Stack>
            </StyledToolbar>
        </StyledRoot>
    );
}
